import { v4 as uuidv4 } from "uuid"

import { env } from "@/lib/env"
import { mtLocation } from "./mt-location"

declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: {
          clientId: string
          scope: string
          redirectURI: string
          state?: string
          usePopup?: boolean
        }) => void
        signIn: () => Promise<{
          authorization: {
            code: string
            id_token: string
            state: string
          }
          // this is only populated the first time the user signs in with apple
          // we should always be able to get the email from the id_token after decoding it
          user?: {
            email: string
            name: {
              firstName: string
              lastName: string
            }
          }
        }>
      }
    }
  }
}

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"

export async function setupAppleSignIn(): Promise<void> {
  const clientId = env("NEXT_PUBLIC_APPLE_SIGN_IN_SERVICE_ID")

  // Skip initialization if clientId is not configured (expected in local dev)
  if (!clientId) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = APPLE_SCRIPT_SRC
    script.async = true
    script.onload = () => {
      try {
        if (!window?.AppleID?.auth) {
          reject(
            new Error(
              "Apple Sign In script loaded, but window.AppleID.auth is not available"
            )
          )
          return
        }

        // some helpful info here; https://developer.apple.com/forums/thread/130666
        window.AppleID.auth.init({
          clientId,
          scope: "name email",
          redirectURI: mtLocation.origin,
          usePopup: true,
          state: uuidv4(),
        })
        resolve()
      } catch (error) {
        console.error("Error initializing Apple Sign-In:", error)
        reject(
          new Error("Failed to initialize Apple Sign-In", {
            cause: error,
          })
        )
      }
    }
    script.onerror = () =>
      reject(new Error("Failed to load Apple Sign In script"))
    script.onabort = () =>
      reject(new Error("Aborted loading Apple Sign In script"))
    document.body.appendChild(script)
  })
}
